import { PluginCategory } from '../types/PluginInterface';

/**
 * Générateur de placeholders uniformes pour tous les plugins modulaires
 */

export interface PlaceholderConfig {
  pluginId: string;
  pluginType: string;
  title: string;
  description?: string;
  icon: string;
  category: PluginCategory; 
  config?: any;
  stats?: string[];
  additionalInfo?: string;
}

/**
 * Génère un placeholder HTML élégant et uniforme pour tous les plugins
 */
export function generateUniformPlaceholder(config: PlaceholderConfig): string {
  const {
    pluginId,
    pluginType,
    title,
    description,
    icon,
    category,
    config: pluginConfig,
    stats = [],
    additionalInfo
  } = config;

  // Palette de couleurs par catégorie
  const categoryColors: Record<string, string> = {
    media: '#e83e8c',        // Rose
    interactive: '#6610f2',  // Violet
    interaction: '#6610f2',  // Alias
    data: '#17a2b8',         // Cyan
    commerce: '#28a745',     // Vert
    ecommerce: '#28a745',    // Alias
    utility: '#ffc107',      // Jaune
    productivity: '#fd7e14', // Orange
    social: '#007bff',       // Bleu
    business: '#343a40',     // Gris foncé
  };

  const categoryColor = categoryColors[category] || '#007bff';

  // Générer les statistiques
  const statsHtml = stats.length > 0 ? `
    <div style="
      background-color: rgba(${hexToRgb(categoryColor)}, 0.1);
      border: 1px solid rgba(${hexToRgb(categoryColor)}, 0.2);
      border-radius: 6px;
      padding: 8px 12px;
      margin-top: 8px;
      font-size: 12px;
      color: #495057;
    ">
      ${stats.join('<br />')}
    </div>
  ` : '';

  return `
    <div 
      class="plugin-container mceNonEditable"
      contenteditable="false"
      data-plugin-id="${pluginId}" 
      data-plugin="${pluginType}"
      ${pluginConfig ? `data-config='${JSON.stringify(pluginConfig).replace(/'/g, '&#39;').replace(/"/g, '&quot;')}'` : ''}
      style="
        border: 2px dashed ${categoryColor};
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-radius: 8px;
        padding: 20px;
        margin: 15px 0;
        text-align: center;
        position: relative;
        min-height: 120px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        user-select: none;
      "
    >
      <!-- Icône du plugin -->
      <div style="
        font-size: 48px;
        margin-bottom: 10px;
        color: ${categoryColor};
      ">${icon}</div>
      
      <!-- Titre du plugin -->
      <h4 style="
        margin: 0 0 8px 0;
        color: ${categoryColor};
        font-size: 18px;
        font-weight: 600;
      ">${title}</h4>
      
      <!-- Description -->
      ${description ? `
        <p style="
          margin: 0 0 10px 0;
          color: #6c757d;
          font-size: 14px;
          font-style: italic;
        ">${description}</p>
      ` : ''}
      
      <!-- Statistiques/Informations -->
      ${statsHtml}
      
      <!-- Information additionnelle -->
      ${additionalInfo ? `
        <div style="
          margin-top: 8px;
          font-size: 12px;
          color: #6c757d;
        ">
          ${additionalInfo}
        </div>
      ` : ''}
      
      <!-- Note pour l'éditeur -->
      <small style="
        display: block;
        margin-top: 12px;
        padding: 8px 12px;
        background-color: rgba(255, 255, 255, 0.9);
        border-radius: 6px;
        font-size: 11px;
        color: #6c757d;
        border: 1px solid #dee2e6;
        text-align: center;
      ">
        📝 Aperçu éditeur - Le composant complet sera affiché dans le rendu final
      </small>
      
      <!-- ✅ CORRECTION : pointer-events: auto, icônes FontAwesome, Textes demandés et Data Attributes -->
      <div style="
        position: absolute;
        top: 8px;
        right: 8px;
        display: flex;
        gap: 6px;
        z-index: 1000;
        pointer-events: auto;
      ">
        
        <button 
            type="button" 
            class="plugin-edit-btn" 
            data-plugin-id="${pluginId}" 
            data-plugin-type="${pluginType}"
            ${pluginConfig ? `data-plugin-config='${JSON.stringify(pluginConfig).replace(/'/g, '&#39;').replace(/"/g, '&quot;')}'` : ''}
            style="
              background: #198754; 
              color: white; 
              border: none; 
              border-radius: 50%;
              width: 16px;
              height: 16px;
              padding: 0;
              font-size: 9px; 
              line-height: 1;
              cursor: pointer; 
              display: flex; 
              align-items: center; 
              justify-content: center;
              font-family: 'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',sans-serif;
              box-shadow: 0 1px 2px rgba(0,0,0,0.2);
              transition: all 0.2s;
            " 
            title="Éditer ce plugin"
            onmouseover="this.style.width='18px'; this.style.height='18px'; this.style.fontSize='10px';"
            onmouseout="this.style.width='16px'; this.style.height='16px'; this.style.fontSize='9px';"
        >
            ✏️
        </button>

        <button 
            type="button" 
            class="plugin-remove-btn" 
            data-plugin-id="${pluginId}" 
            style="
              background: #dc3545; 
              color: white; 
              border: none; 
              border-radius: 50%;
              width: 16px;
              height: 16px;
              padding: 0;
              font-size: 9px; 
              line-height: 1;
              cursor: pointer; 
              display: flex; 
              align-items: center; 
              justify-content: center;
              font-family: 'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',sans-serif;
              box-shadow: 0 1px 2px rgba(0,0,0,0.2);
              transition: all 0.2s;
            " 
            title="Supprimer ce plugin"
            onmouseover="this.style.width='18px'; this.style.height='18px'; this.style.fontSize='10px';"
            onmouseout="this.style.width='16px'; this.style.height='16px'; this.style.fontSize='9px';"
        >
            🗑️
        </button>
      </div>
    </div>
  `;
  
 }

/**
 * Convertit une couleur hex en RGB
 */
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `${r}, ${g}, ${b}`;
  }
  return '0, 123, 255'; // Fallback blue
}
